// KL Rating page - displays and allows editing of ratings for KL (food) events
// Only certain users (Matias, Glenn) can edit ratings
// Higher ratings mean better quality events

import React, { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore'; // Firebase database
import { getAuth } from 'firebase/auth'; // Firebase authentication
import { app } from './firebase-config'; // Firebase configuration

const parseRatingValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const normalized = value.replace(',', '.').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getEventRating = (event) => {
  return parseRatingValue(
    event.rating ?? event.kl_rating ?? event.klRating ?? event.Rating
  );
};

const KlRating = () => {
  // State variables
  const [events, setEvents] = useState([]); // All KL events
  const [loading, setLoading] = useState(true); // Whether data is still loading
  const [isEditing, setIsEditing] = useState(false); // Whether ratings are in edit mode
  const [userName, setUserName] = useState(null); // Current logged-in user's name

  // Only these users can edit ratings
  const allowedUsers = ["Matias", "Glenn"];
  
  // This effect runs when component loads - checks who is logged in
  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        const db = getFirestore(app);
        const userDocRef = doc(db, 'users', currentUser.uid); // Get user's data from database
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log("Current User Name: ", userData.name);
          setUserName(userData.name); // Store the user's name
        } else {
          console.error('User document not found.');
          setUserName(null);
        }
      } else {
        console.log("No user is signed in.");
        setUserName(null);
      }
    });
    return () => unsubscribe(); // Clean up when component unmounts
  }, []);

  // This effect runs when component loads - fetches all KL events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const db = getFirestore(app);
        const eventsCollection = collection(db, 'events'); // Get all events from database
        const eventsSnapshot = await getDocs(eventsCollection);
        const eventsList = eventsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data() // Spread all the event data
        }));

        // Filter to only show KL (food) events and sort by rating (highest first)
        const filteredEvents = eventsList
          .filter(event => event.event_type === 'KL🥩')
          .map((event) => ({
            ...event,
            rating: getEventRating(event)
          }));
        filteredEvents.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1)); // Sort descending by rating
        
        setEvents(filteredEvents);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching events: ', error);
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Function to toggle edit mode for ratings
  const handleEditClick = () => {
    setIsEditing(!isEditing);
  };

  // Function to update a rating in the local state
  const handleRatingChange = (eventId, newRating) => {
    const parsedRating = parseRatingValue(newRating);
    setEvents(events.map(event => 
      event.id === eventId ? { ...event, rating: parsedRating } : event
    ));
  };

  // Function to save all edited ratings to the database
  const handleSetClick = async () => {
    const db = getFirestore(app);
    try {
      // Update each event's rating in the database
      for (const event of events) {
        const eventDocRef = doc(db, 'events', event.id);
        await updateDoc(eventDocRef, { rating: event.rating });
      }
      setIsEditing(false); // Exit edit mode
      alert('KL-ratings lagret!'); // Show success message
    } catch (error) {
      console.error('Error updating ratings: ', error);
      alert('Error updating ratings. Please try again.');
    }
  };

  // Show loading message while fetching data
  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <h1>🏆KL-ranking🏆</h1>
      <p>KL-ratings blir gjort av Glenn, eller med fullmakt fra Glenn, basert på mat, drikke og stemning. </p>
      {events.length === 0 ? (
        <p>Logg inn på "Min profil" for å se KL-ratings</p>
      ) : (
        <div>
          {allowedUsers.includes(userName) && (
            <button className="button-17" onClick={isEditing ? handleSetClick : handleEditClick}>
              {isEditing ? 'Lagre' : 'Rediger KL-ratings'}
            </button>
          )}
          <table>
            <thead>
              <tr>
                <th>Sted/ Konsept</th>
                <th>Event Type</th>
                <th>Kvartal</th>
                <th>🏆Rating🏆</th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.id}>
                  <td>{event.sted_konsept}</td>
                  <td>{event.event_type}</td>
                  <td>{event.Kvartal}</td>
                  <td>
                    {isEditing && allowedUsers.includes(userName) ? (
                      <input 
                        type="number"
                        step="0.01"
                        value={event.rating}
                        onChange={(e) => handleRatingChange(event.id, e.target.value)}
                        className="rating-input"
                      />
                    ) : (
                      event.rating ?? ''
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default KlRating;
